// src/pages/ManagedAuthorFormPage.jsx
// Phase P2: Create/Edit Managed Author Form with AI Bio Enhancement
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PublisherNav from '../components/PublisherNav';
import { User, ArrowLeft, Save, Upload, ImagePlus, Info, AlertCircle, Sparkles, X, Check, Volume2, Loader2, Square } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useNotification } from '@/contexts/NotificationContext';
import { BACKEND_URL as backendUrl } from '@/config/api';

const ManagedAuthorFormPage = () => {
  const navigate = useNavigate();
  const { authorId } = useParams();
  const isEditing = Boolean(authorId);
  const fileInputRef = useRef(null);
  
  const { notifyError } = useNotification();
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  
  // Bio enhancement state
  const [enhancing, setEnhancing] = useState(false);
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [enhancedBio, setEnhancedBio] = useState('');
  const [originalBio, setOriginalBio] = useState('');
  
  // Read Aloud state
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    pen_name: '',
    real_name: '',
    about_text: '',
    status: 'active'
  });
  
  // Headshot state
  const [headshotUrl, setHeadshotUrl] = useState(null);
  const [headshotPreview, setHeadshotPreview] = useState(null);
  const [fallbackUrl, setFallbackUrl] = useState(null);
  
  const [errors, setErrors] = useState({});
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    if (isEditing) {
      fetchAuthor();
    }
  }, [authorId]);

  const fetchAuthor = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/authors/${authorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({
          pen_name: data.pen_name || '',
          real_name: data.real_name || '',
          about_text: data.about_text || '',
          status: data.status || 'active'
        });
        
        if (data.headshot_image_url) {
          setHeadshotUrl(`${backendUrl}${data.headshot_image_url}?t=${Date.now()}`);
        }
        if (data.headshot_fallback_url) {
          setFallbackUrl(`${backendUrl}${data.headshot_fallback_url}?t=${Date.now()}`);
        }
      } else {
        notifyError('Failed to load author');
        navigate('/publisher/authors');
      }
    } catch (error) {
      console.error('Error fetching author:', error);
      notifyError('Failed to load author');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Bio enhancement handler
  const handleEnhanceBio = async () => {
    if (!formData.about_text.trim()) {
      notifyError('Please enter some bio text before enhancing');
      return;
    }
    
    setEnhancing(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/authors/${authorId}/enhance-bio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ current_bio: formData.about_text })
      });
      
      if (response.ok) {
        const data = await response.json();
        setOriginalBio(data.original_bio);
        setEnhancedBio(data.enhanced_bio);
        setShowEnhanceModal(true);
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to enhance bio');
      }
    } catch (error) {
      console.error('Error enhancing bio:', error);
      notifyError('Failed to enhance bio');
    } finally {
      setEnhancing(false);
    }
  };

  const handleAcceptEnhancedBio = () => {
    setFormData(prev => ({ ...prev, about_text: enhancedBio }));
    setShowEnhanceModal(false);
    setAudioUrl(null); // Reset audio when bio changes
    console.log('[SUCCESS] ' + 'Enhanced bio applied');
  };

  // Read Aloud handler
  const handleReadAloud = async () => {
    if (!formData.about_text.trim()) {
      notifyError('No bio text to read');
      return;
    }
    
    // If already playing, stop
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      return;
    }
    
    // If we have cached audio, just play it
    if (audioUrl) {
      playAudio();
      return;
    }
    
    // Generate new audio
    setGeneratingAudio(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${backendUrl}/api/publisher/authors/${authorId}/bio-tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullAudioUrl = `${backendUrl}${data.audio_url}?token=${token}`;
        setAudioUrl(fullAudioUrl);
        
        // Play after short delay to let state update
        setTimeout(() => playAudio(fullAudioUrl), 100);
        
        if (!data.cached) {
          console.log('[SUCCESS] ' + `Audio generated ($${data.cost?.toFixed(4) || '0.00'})`);
        }
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to generate audio');
      }
    } catch (error) {
      console.error('Error generating bio audio:', error);
      notifyError('Failed to generate audio');
    } finally {
      setGeneratingAudio(false);
    }
  };
  
  const playAudio = (url = audioUrl) => {
    if (!url) return;
    
    const token = localStorage.getItem('accessToken');
    const audioUrlWithAuth = url.includes('token=') ? url : `${url}?token=${token}`;
    
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.onerror = () => {
        setIsPlaying(false);
        notifyError('Error playing audio');
      };
    }
    
    audioRef.current.src = audioUrlWithAuth;
    audioRef.current.play()
      .then(() => setIsPlaying(true))
      .catch(err => {
        console.error('Error playing audio:', err);
        notifyError('Failed to play audio');
      });
  };

  // Headshot upload handlers
  const handleHeadshotClick = () => {
    fileInputRef.current?.click();
  };

  const handleHeadshotSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notifyError('Invalid file type. Please upload PNG, JPG, or WebP.');
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      notifyError('File too large. Maximum size is 2MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setHeadshotPreview(e.target?.result);
    reader.readAsDataURL(file);

    // Upload if editing existing author
    if (isEditing) {
      await uploadHeadshot(file);
    }
  };

  const uploadHeadshot = async (file) => {
    setUploadingHeadshot(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('headshot', file);

      const response = await fetch(`${backendUrl}/api/publisher/authors/${authorId}/headshot`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setHeadshotUrl(`${backendUrl}${data.headshot_image_url}?t=${Date.now()}`);
        setHeadshotPreview(null);
        console.log('[SUCCESS] ' + 'Headshot uploaded successfully');
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to upload headshot');
        setHeadshotPreview(null);
      }
    } catch (error) {
      console.error('Error uploading headshot:', error);
      notifyError('Failed to upload headshot');
      setHeadshotPreview(null);
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.pen_name.trim()) {
      newErrors.pen_name = 'Pen Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      notifyError('Please fix the errors before submitting');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing 
        ? `${backendUrl}/api/publisher/authors/${authorId}`
        : `${backendUrl}/api/publisher/authors`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        console.log('[SUCCESS] ' + isEditing ? 'Author updated successfully' : 'Author created successfully');
        navigate('/publisher/authors');
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to save author');
      }
    } catch (error) {
      console.error('Error saving author:', error);
      notifyError('Failed to save author');
    } finally {
      setSaving(false);
    }
  };

  const currentHeadshot = headshotPreview || headshotUrl;
  const displayImage = currentHeadshot || fallbackUrl;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
            <p className="mt-4 text-slate-400">Loading author...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-3xl px-4 py-8">
        {/* Back button */}
        <Link
          to="/publisher/authors"
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Authors
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-blue-600/20 p-2">
              <User className="w-6 h-6 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Edit Author' : 'Add New Author'}
            </h1>
          </div>
          <p className="text-slate-400">
            {isEditing 
              ? 'Update author information and "About the Author" content.'
              : 'Create a new author profile for your publishing catalog.'
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Author Headshot Section */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Author Headshot</h2>
            <div className="flex items-start gap-6">
              {/* Headshot Preview/Upload */}
              <div className="relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleHeadshotSelect}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                />
                {displayImage ? (
                  <div className="relative group">
                    <img
                      src={displayImage}
                      alt="Author Headshot"
                      className="w-32 h-32 rounded-xl object-cover border border-slate-600"
                    />
                    <button
                      type="button"
                      onClick={handleHeadshotClick}
                      disabled={!isEditing}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity disabled:cursor-not-allowed"
                    >
                      <Upload className="w-6 h-6 text-white" />
                    </button>
                    {uploadingHeadshot && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-xl">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                      </div>
                    )}
                    {!headshotUrl && fallbackUrl && (
                      <div className="absolute -bottom-2 -right-2 bg-slate-700 rounded-full px-2 py-0.5 text-xs text-slate-300">
                        Fallback
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleHeadshotClick}
                    disabled={!isEditing && uploadingHeadshot}
                    className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-600 bg-slate-800 flex flex-col items-center justify-center hover:border-blue-500 hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    {uploadingHeadshot ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                    ) : (
                      <>
                        <ImagePlus className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-xs text-slate-400">Upload</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* Guidelines */}
              <div className="flex-1 text-sm text-slate-400">
                <p className="font-medium text-slate-300 mb-2">Headshot Guidelines</p>
                <ul className="space-y-1 text-xs">
                  <li>• Accepted formats: PNG, JPG, WebP</li>
                  <li>• Maximum file size: 2MB</li>
                  <li>• Recommended: Square image (e.g., 512x512)</li>
                  <li>• If no headshot is uploaded, your imprint logo will be used as fallback</li>
                </ul>
                {!isEditing && (
                  <p className="mt-3 text-xs text-amber-400">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Save the author first to upload a headshot
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Author Identity */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Author Identity</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="pen_name">Pen Name *</Label>
                <Input
                  id="pen_name"
                  name="pen_name"
                  value={formData.pen_name}
                  onChange={handleChange}
                  placeholder="The name readers will see"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.pen_name ? 'border-red-500' : ''}`}
                />
                {errors.pen_name && (
                  <p className="text-red-400 text-xs mt-1">{errors.pen_name}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  This is the public-facing name that will appear on titles and reader views.
                </p>
              </div>
              
              <div>
                <Label htmlFor="real_name">Real Name (Internal Only)</Label>
                <Input
                  id="real_name"
                  name="real_name"
                  value={formData.real_name}
                  onChange={handleChange}
                  placeholder="Optional - for your records only"
                  className="mt-1 bg-slate-800 border-slate-600"
                />
                <div className="flex items-start gap-2 mt-1">
                  <Info className="w-3 h-3 text-slate-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-slate-500">
                    This name is only visible within your Publisher console. It will never be shown to readers.
                  </p>
                </div>
              </div>
              
              {isEditing && (
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              )}
            </div>
          </section>

          {/* About the Author */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">About the Author</h2>
              {isEditing && (
                <div className="flex items-center gap-2">
                  {/* Read Aloud Button */}
                  <button
                    type="button"
                    onClick={handleReadAloud}
                    disabled={generatingAudio || !formData.about_text.trim()}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-700 hover:bg-slate-600 px-4 py-1.5 text-sm font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isPlaying ? "Stop" : "Read Aloud"}
                  >
                    {generatingAudio ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating...
                      </>
                    ) : isPlaying ? (
                      <>
                        <Square className="w-4 h-4" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-4 h-4" />
                        Read Aloud
                      </>
                    )}
                  </button>
                  
                  {/* Enhance with AI Button */}
                  <button
                    type="button"
                    onClick={handleEnhanceBio}
                    disabled={enhancing || !formData.about_text.trim()}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 px-4 py-1.5 text-sm font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {enhancing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Enhance with AI
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-400 mb-4">
              This content will be displayed in the reader-facing "About the Author" section.
            </p>
            <Textarea
              id="about_text"
              name="about_text"
              value={formData.about_text}
              onChange={handleChange}
              placeholder="Write a compelling author bio that will engage readers..."
              rows={12}
              className="bg-slate-800 border-slate-600 resize-none"
            />
            <p className="text-xs text-slate-500 mt-2">
              Tip: Include the author's background, writing style, and notable achievements.
              {isEditing && " Use the 'Enhance with AI' button to generate a polished 300+ word professional bio with paragraph breaks."}
            </p>
          </section>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Link
              to="/publisher/authors"
              className="px-6 py-2 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Author' : 'Create Author'}
                </>
              )}
            </button>
          </div>
        </form>
      </main>

      {/* Enhanced Bio Preview Modal */}
      <Dialog open={showEnhanceModal} onOpenChange={setShowEnhanceModal}>
        <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-700 text-slate-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Enhanced Author Bio
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Review the AI-enhanced bio below. Accept to replace the current bio, or dismiss to keep the original.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            {/* Original Bio */}
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-2">Original Bio</h4>
              <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-4 text-sm text-slate-300 max-h-32 overflow-y-auto whitespace-pre-wrap">
                {originalBio}
              </div>
            </div>
            
            {/* Enhanced Bio */}
            <div>
              <h4 className="text-sm font-medium text-purple-400 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Enhanced Bio (300+ words)
              </h4>
              <div className="rounded-lg bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border border-purple-500/30 p-4 text-sm text-slate-200 max-h-64 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                {enhancedBio}
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-3">
            <button
              onClick={() => setShowEnhanceModal(false)}
              className="flex items-center gap-2 px-5 py-2 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              <X className="w-4 h-4" />
              Dismiss
            </button>
            <button
              onClick={handleAcceptEnhancedBio}
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all"
            >
              <Check className="w-4 h-4" />
              Accept Enhanced Bio
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UniversalFooter />
    </div>
  );
};

export default ManagedAuthorFormPage;
